import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Light / dark mode context for the app.
 *
 * Defaults to the OS `prefers-color-scheme` setting on first visit; once the
 * user clicks the toggle, the choice is stored in localStorage and survives
 * across sessions. The system preference is only consulted when no explicit
 * override is set.
 */
const STORAGE_KEY = 'prompt_security_theme_mode';

const detectSystemMode = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const readStoredMode = () => {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
};

const ThemeModeContext = createContext({
  mode: 'light',
  setMode: () => {},
  toggle: () => {},
});

export const ThemeModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => readStoredMode() || detectSystemMode());

  // Persist whenever the user overrides.
  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore quota / privacy-mode errors; runtime state stays in sync
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  // If the user hasn't picked a mode, follow OS changes live.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    if (readStoredMode()) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setModeState(e.matches ? 'dark' : 'light');
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const value = useMemo(() => ({ mode, setMode, toggle }), [mode, setMode, toggle]);
  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => useContext(ThemeModeContext);
